/**
 * Generated-route card with load summaries, trip-type switch, and reorder controls.
 *
 * @remarks
 * `../../review-dialog.tsx` embeds this presentation component. Route edits are delegated
 * through callbacks to the review hook; local state only supports preview controls.
 *
 * @packageDocumentation
 */
import {Alert, Button, Icon, List, Switch, Tooltip, Typography} from 'antd';
import React, {useState} from 'react';
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd';
import {FlightLeg} from '../../../../../../schema';
import './route-preview.less';
import {FlightRouteCard, FlightRouteLegContent} from '../../../flight-route-display';

interface AutoAssignRoutePreviewProps {
    readOnly?: boolean;
    legs: FlightLeg[];
    passengerCount: number;
    cargoCount: number;
    omittedPassengerCount?: number;
    omittedCargoCount?: number;
    returnToOrigin: boolean;
    returnLocationName?: string;
    onReturnToOriginChange?: (returnToOrigin: boolean) => void;
    onReorder?: (fromLegIndex: number, toLegIndex: number) => string | undefined;
    canUndo?: boolean;
    canRedo?: boolean;
    onUndo?: () => void;
    onRedo?: () => void;
}

/**
 * Read-only confirmation content for an auto-generated route. Ant Design
 * primitives keep it consistent with the rest of Manifest Central while
 * exposing the operational details a dispatcher should review before saving.
 */
export default function AutoAssignRoutePreview(props: AutoAssignRoutePreviewProps) {
    const {
        legs,
        passengerCount,
        cargoCount,
        omittedPassengerCount = 0,
        omittedCargoCount = 0,
        returnToOrigin,
        returnLocationName,
        onReturnToOriginChange,
        onReorder,
        canUndo = false,
        canRedo = false,
        onUndo,
        onRedo
    } = props;
    const [reorderError, setReorderError] = useState<string>();

    const positioningLegCount = legs.filter((leg) => (
        !(leg.paxIDs || []).length && !(leg.cgoIDs || []).length
    )).length;
    const omittedCount = omittedPassengerCount + omittedCargoCount;
    const needsReview = positioningLegCount > 0 || omittedCount > 0;

    const coverageDescription = `${passengerCount} ${passengerCount === 1 ? 'passenger' : 'passengers'} and ` +
        `${cargoCount} cargo ${cargoCount === 1 ? 'item is' : 'items are'} covered by this route.`;
    const omittedDescription = omittedCount
        ? ` ${omittedCount} incompatible ${omittedCount === 1 ? 'item will' : 'items will'} remain unassigned.`
        : '';
    let description = coverageDescription;
    if (positioningLegCount) {
        description += ` ${positioningLegCount} empty positioning ${positioningLegCount === 1 ? 'leg requires' : 'legs require'} review.`;
    }
    description += omittedDescription;
    // Keep room for the positioning warning when the return leg is toggled off.
    const reservedPositioningCount = Math.max(1, positioningLegCount);

    const finalLegIndex = legs.length - 1;
    const hasProtectedReturn = returnToOrigin && legs.length > 0 &&
        legs[finalLegIndex].destinationID === legs[0].departureID;
    const returnLabel = returnLocationName || legs[0]?.departure || legs[0]?.departureID || 'origin';

    function reorder(fromLegIndex: number, toLegIndex: number) {
        if (!onReorder || fromLegIndex === toLegIndex) return;
        const error = onReorder(fromLegIndex, toLegIndex);
        setReorderError(error);
    }

    function onDragEnd(result: DropResult) {
        if (!result.destination) return;
        reorder(result.source.index, result.destination.index);
    }

    function undo() {
        setReorderError(undefined);
        onUndo?.();
    }

    function redo() {
        setReorderError(undefined);
        onRedo?.();
    }

    return (
        <div className="auto-assign-route-preview">
            <Alert
                type={needsReview ? 'warning' : 'success'}
                showIcon
                message={needsReview ? 'Review recommended' : 'Route ready for review'}
                description={(
                    <div className="auto-assign-route-preview__summary-description">
                        {['leg requires', 'legs require'].map(wording => (
                            <span key={wording} className="auto-assign-route-preview__summary-reserve" aria-hidden="true">
                                {`${coverageDescription} ${reservedPositioningCount} empty positioning ${wording} review.${omittedDescription}`}
                            </span>
                        ))}
                        <span>{description}</span>
                    </div>
                )}
                className="auto-assign-route-preview__summary"
            />

            <FlightRouteCard
                className="auto-assign-route-preview__route-card"
                title="Generated route"
                legCount={legs.length}
                headerExtra={(onUndo || onRedo) ? (
                    <Button.Group>
                        <Tooltip title="Undo preview change">
                            <Button
                                size="small"
                                icon="undo"
                                aria-label="Undo preview change"
                                disabled={!canUndo}
                                onClick={undo}
                            />
                        </Tooltip>
                        <Tooltip title="Redo preview change">
                            <Button
                                size="small"
                                icon="redo"
                                aria-label="Redo preview change"
                                disabled={!canRedo}
                                onClick={redo}
                            />
                        </Tooltip>
                    </Button.Group>
                ) : undefined}
            >
                <div className="auto-assign-route-preview__return-option">
                    <div className="auto-assign-route-preview__return-copy">
                        <Typography.Text strong>Trip type</Typography.Text>
                        <Typography.Text type="secondary">
                            {returnToOrigin
                                ? `The route is a round trip and ends back at ${returnLabel}.`
                                : 'The route is one way and ends at the final stop.'}
                        </Typography.Text>
                    </div>
                    <Switch
                        disabled={props.readOnly}
                        checked={returnToOrigin}
                        checkedChildren="Round trip"
                        unCheckedChildren="One way"
                        aria-label={`Choose round trip or one way for ${returnLabel}`}
                        onChange={onReturnToOriginChange}
                    />
                </div>

                {onReorder && legs.length > 1 ? (
                    <Alert
                        className="auto-assign-route-preview__reorder-help"
                        type="info"
                        showIcon
                        message="Drag a leg row or use its arrow controls to change the stop order."
                    />
                ) : null}

                {reorderError ? (
                    <Alert
                        className="auto-assign-route-preview__reorder-error"
                        type="warning"
                        showIcon
                        message={reorderError}
                    />
                ) : null}

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="auto-assign-route-preview">
                        {(dropProvided) => (
                            <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                <List
                                    size="small"
                                    className="auto-assign-route-preview__route-list"
                                    dataSource={legs}
                                    locale={{emptyText: 'No flight legs will be created'}}
                                    renderItem={(leg, index) => {
                                        const returnLocked = hasProtectedReturn && index === finalLegIndex;
                                        const canDrag = !!onReorder && !returnLocked;

                                        return (
                                            <Draggable
                                                key={`route-leg-${index}-${leg.departureID}-${leg.destinationID}`}
                                                draggableId={`route-leg-${index}-${leg.departureID}-${leg.destinationID}`}
                                                index={index}
                                                isDragDisabled={!onReorder || returnLocked}
                                            >
                                                {(dragProvided, dragSnapshot) => (
                                                    <div
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        style={dragProvided.draggableProps.style}
                                                        className={dragSnapshot.isDragging ? 'auto-assign-route-preview__draggable auto-assign-route-preview__draggable--active' : 'auto-assign-route-preview__draggable'}
                                                    >
                                                        <List.Item
                                                            {...(canDrag && dragProvided.dragHandleProps ? dragProvided.dragHandleProps : {})}
                                                            className={canDrag
                                                                ? 'auto-assign-route-preview__leg auto-assign-route-preview__leg--draggable'
                                                                : 'auto-assign-route-preview__leg'}
                                                            aria-label={canDrag
                                                                ? `Drag leg ${index + 1}: ${leg.departure || leg.departureID} to ${leg.destination || leg.destinationID}`
                                                                : undefined}
                                                        >
                                                            <FlightRouteLegContent
                                                                leg={leg}
                                                                index={index}
                                                                totalLegs={legs.length}
                                                            />

                                                            {onReorder ? (
                                                                <div
                                                                    className="auto-assign-route-preview__reorder-actions"
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onTouchStart={(event) => event.stopPropagation()}
                                                                    onKeyDown={(event) => event.stopPropagation()}
                                                                >
                                                                    {returnLocked ? (
                                                                        <Tooltip title="Final return leg cannot be reordered">
                                                                            <Icon type="lock" className="auto-assign-route-preview__lock" aria-label="Final return leg cannot be reordered"/>
                                                                        </Tooltip>
                                                                    ) : null}
                                                                    <Button.Group>
                                                                        <Tooltip title="Move earlier">
                                                                            <Button
                                                                                size="small"
                                                                                icon="up"
                                                                                aria-label={`Move leg ${index + 1} earlier`}
                                                                                disabled={index === 0 || returnLocked}
                                                                                onClick={() => reorder(index, index - 1)}
                                                                            />
                                                                        </Tooltip>
                                                                        <Tooltip title="Move later">
                                                                            <Button
                                                                                size="small"
                                                                                icon="down"
                                                                                aria-label={`Move leg ${index + 1} later`}
                                                                                disabled={index === finalLegIndex || (hasProtectedReturn && index === finalLegIndex - 1)}
                                                                                onClick={() => reorder(index, index + 1)}
                                                                            />
                                                                        </Tooltip>
                                                                    </Button.Group>
                                                                </div>
                                                            ) : null}
                                                        </List.Item>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    }}
                                />
                                {dropProvided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </FlightRouteCard>

        </div>
    );
}
